import { Box, SxProps, Theme, Typography } from "@mui/material";
import type { ChartData, ChartOptions, Scale, TooltipModel } from "chart.js";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Line } from "react-chartjs-2";

import { asPercentage, asUsDate } from "components/ui/formatters";
import "helpers/chartjs";
import { BucketPerformancePoint } from "helpers/types";
import {
  chartConfig,
  chartsReportStyles,
  performanceChartStyles,
  performanceTickFont,
  performanceYAxisConfig,
} from "styles/ChartsReport.styles";
import { ChartSpec } from "./chartSeriesSelection";
import { OverlayLine, buildOverlayChartData } from "./overlayChartData";

interface PerformanceLineChartProps {
  /** The section's heading — the chart carries every line, so it names none. */
  title: string;
  /** One spec per selected series. They are drawn overlaid, in this order. */
  specs: ChartSpec[];
}

/**
 * The Y axis bounds for the overlaid series, fitted to them rather than left
 * to the chart library.
 *
 * Fitted to the lines' own range so a period that moved a few percent fills
 * the plot rather than sitting as a flat stroke on an axis sized by the
 * library's own idea of round numbers.
 *
 * The baseline point is always 0% (the series are cumulative returns — see
 * overlayChartData's asCumulativeReturn), so the fitted range always contains
 * where the period started and a bucket that only fell still reads against it. The
 * padding has a floor so a period that barely moved is not magnified into
 * dramatic-looking noise across the whole plot.
 *
 * Fitted across EVERY line at once now that they share one set of axes: a
 * range fitted to one of them would push the others off the plot.
 *
 * REDUCED, never `Math.min(...totals)`: argument spread is bounded by the
 * engine's call stack, and the series is as long as the period the reader
 * picked, so a long enough one threw RangeError during render and blanked the
 * whole report instead of degrading the chart.
 *
 * Exported for that last reason — the bound is what needs pinning at a size
 * no chart library will render.
 */
export const fitPercentAxis = (
  points: BucketPerformancePoint[],
): { min: number; max: number } | null => {
  if (points.length === 0) return null;
  let low = points[0].total;
  let high = points[0].total;
  for (const point of points) {
    if (point.total < low) low = point.total;
    if (point.total > high) high = point.total;
  }
  const pad = Math.max((high - low) * 0.08, 0.5);
  return { min: low - pad, max: high + pad };
};

/**
 * A percentage carrying its sign, so a gain reads "+12.00%" beside a loss.
 *
 * Signed on the figure as SHOWN, to two places: a total re-based from the
 * index carries float residue (100.001 - 100), and signing the raw value
 * would print "+0.00%" or "-0.00%" for what reads as no move at all.
 */
export const formatSigned = (value: number): string => {
  const shown = Number(value.toFixed(2)) || 0;
  return shown > 0 ? `+${asPercentage(shown)}` : asPercentage(shown);
};

/**
 * A day's move, signed. The baseline point has none — the period is measured
 * FROM it, so there is no previous day it moved against — and an em dash
 * says that, where a "+0.00%" would claim a flat day the series never had.
 */
export const formatDaily = (daily: number | null): string =>
  daily === null ? "—" : formatSigned(daily);

/**
 * A line's SVG `stroke-dasharray` as Chart.js' `borderDash`.
 *
 * The two describe the same thing in different types — "7 4" against [7, 4] —
 * and the pattern is authored once, in seriesDashPatterns, as the SVG string
 * the legend swatch draws with. This is the one-way translation to the canvas,
 * so the legend and the line cannot drift apart. A solid line is [], which
 * Chart.js reads as "no pattern".
 */
export const dashArray = (dash: string): number[] =>
  dash
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number)
    .filter((segment) => Number.isFinite(segment));

/**
 * A line's colour at the fill's opacity.
 *
 * The fill under a lone line is the SAME colour as its stroke, softened — the
 * mockup's look, which x-charts gave as a `fill-opacity` on an SVG path. A
 * canvas has no such handle: the fill is a colour, so the opacity has to be
 * carried in it. Hex in, `rgba()` out; anything not a 6-digit hex is handed
 * back untouched rather than mangled into an invalid colour.
 */
export const withAlpha = (color: string, alpha: number): string => {
  const hex = /^#([0-9a-f]{6})$/i.exec(color);
  if (!hex) return color;
  const value = parseInt(hex[1], 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

/**
 * The overlaid lines as Chart.js' dataset list.
 *
 * Exported and pure so the chart's CONFIGURATION is what the suite asserts on.
 * A canvas renders nothing a test can read — jsdom gives it no drawing surface
 * and no layout — so "this series breaks at an unpriced day" is checked here,
 * where it is decided, rather than against pixels that were never painted.
 */
export const buildPerformanceChartData = (
  dates: string[],
  lines: OverlayLine[],
): ChartData<"line", (number | null)[], string> => ({
  labels: dates,
  datasets: lines.map((line) => ({
    // The line's key, so a dataset can be found by the series it draws.
    label: line.title,
    data: line.totals,
    borderColor: line.color,
    borderDash: dashArray(line.dash),
    borderWidth: 2,
    // No point markers: a series is as long as the period the reader picked,
    // and a marker per day turns a line into a smear.
    pointRadius: 0,
    // ...but the point still has to be HOVERABLE, or the tooltip has nothing
    // to report. A hit radius without a drawn marker is exactly that.
    pointHitRadius: 12,
    // The hovered day IS marked, on every line at once: the index-mode
    // interaction makes each line's point at that day active, and Chart.js
    // draws an active point with these. A hollow ring in the line's own
    // colour, so the reader sees which point of each line the tooltip is
    // reporting (operator, task 4180). A line with no priced day there has no
    // point, so it gets no ring — as the tooltip leaves it out.
    pointHoverRadius: performanceChartStyles.hoverRing.radius,
    pointHoverBorderWidth: performanceChartStyles.hoverRing.borderWidth,
    pointHoverBorderColor: line.color,
    pointHoverBackgroundColor: performanceChartStyles.hoverRing.fill,
    cubicInterpolationMode: chartConfig.curve,
    tension: chartConfig.tension,
    // A null in `data` is a day this series was not priced on, and the line is
    // drawn ACROSS it (operator, task 3352). The axis carries only days
    // something was measured on (buildOverlayChartData's axisDates), so a null
    // that survives onto it is one series' own missing day sitting between two
    // days it does have — an Orion gap on a day the market was open. Joining
    // them reads as the gap it is; BREAKING there, which is Chart.js' own
    // default and what this chart shipped with, reads as the end of the series
    // and turned every such day into a hole the reader took for missing data.
    spanGaps: true,
    // The soft fill under the line is kept for a LONE series, which is what
    // the section shipped with. Filled areas of several series sit on top of
    // one another and muddy every line under the topmost, so an overlay of two
    // or more is drawn as lines alone.
    //
    // "origin": filled to the 0% the period started from, so a gain is
    // shaded above it and a loss below it (operator, task 5123). The fitted
    // axis always contains 0% — every series opens there — so the fill never
    // runs off the plot.
    fill: lines.length === 1 ? ("origin" as const) : (false as const),
    backgroundColor: withAlpha(line.color, performanceChartStyles.fillOpacity),
  })),
});

/** Where the pointer is, and which day of the shared axis it is over. */
interface HoveredDay {
  dataIndex: number;
  x: number;
  y: number;
  /** True once the caret is past half the plot: the panel prefers to open leftward. */
  flip: boolean;
}

/** The space between the caret and the near edge of the tooltip panel. */
const TOOLTIP_GAP = 12;

/**
 * Where the tooltip panel's left edge goes, in the plot's own coordinates.
 *
 * The panel keeps its natural width wherever the pointer is (see
 * `tooltipAnchor`), so it is PLACED to fit rather than squeezed to fit. It
 * opens on the side `flip` asks for, unless only the other side has room for
 * it; when neither does — a plot narrower than twice the panel, hovered near
 * the middle — it is slid back inside the plot rather than hung off its edge.
 */
export const tooltipLeft = ({
  caretX,
  flip,
  panelWidth,
  plotWidth,
}: {
  caretX: number;
  flip: boolean;
  panelWidth: number;
  plotWidth: number;
}): number => {
  const rightOfCaret = caretX + TOOLTIP_GAP;
  const leftOfCaret = caretX - TOOLTIP_GAP - panelWidth;
  const fitsRight = rightOfCaret + panelWidth <= plotWidth;
  const fitsLeft = leftOfCaret >= 0;
  const preferred = flip
    ? fitsLeft || !fitsRight
      ? leftOfCaret
      : rightOfCaret
    : fitsRight || !fitsLeft
      ? rightOfCaret
      : leftOfCaret;
  return Math.max(0, Math.min(preferred, plotWidth - panelWidth));
};

const sameDay = (a: HoveredDay | null, b: HoveredDay | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.dataIndex === b.dataIndex &&
    a.x === b.x &&
    a.y === b.y &&
    a.flip === b.flip);

/**
 * The chart's options, built from the shared axis and the fitted bounds.
 *
 * Exported alongside the data for the same reason: the axis fitting, the date
 * formatting and the tooltip's index trigger are decisions this component
 * makes, and they are read here rather than inferred from a blank canvas.
 */
export const buildPerformanceChartOptions = (
  points: BucketPerformancePoint[],
  onHover: (day: HoveredDay | null) => void,
): ChartOptions<"line"> => {
  const fitted = fitPercentAxis(points);
  return {
    responsive: true,
    // The section gives the canvas its height; without this Chart.js would
    // hold a 2:1 box instead and ignore it.
    maintainAspectRatio: false,
    layout: { padding: performanceChartStyles.padding },
    // An AXIS trigger, not a nearest-point one: the tooltip reports the
    // hovered DAY across every line, so hovering anywhere in a day's column
    // has to find all of them. `intersect: false` is what lets the pointer be
    // between two lines rather than on one.
    interaction: { mode: "index", intersect: false, axis: "x" },
    plugins: {
      // Both ours instead: the legend wraps and draws dash patterns, and the
      // tooltip is a table of every line's figures. Chart.js' own are off.
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }: { chart: { width: number }; tooltip: TooltipModel<"line"> }) => {
          if (tooltip.opacity === 0) {
            onHover(null);
            return;
          }
          const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
          if (dataIndex === undefined) {
            onHover(null);
            return;
          }
          onHover({
            dataIndex,
            x: tooltip.caretX,
            y: tooltip.caretY,
            flip: tooltip.caretX > chart.width / 2,
          });
        },
      },
    },
    scales: {
      // The X axis carries the ISO date STRINGS as categories rather than Date
      // objects on a time scale. `new Date("2025-08-28")` is read as UTC
      // midnight, which renders as the previous day anywhere west of Greenwich
      // — the same trap asUsDate exists to avoid — and the stats charts
      // already plot these dates as strings for that reason. The cost is that
      // an unequal calendar gap (a weekend, a holiday) is drawn evenly, which
      // is the right trade for a series whose points ARE the days that were
      // priced.
      x: {
        type: "category",
        // No vertical rules: x-charts drew none, and a rule per priced day is
        // a hatch over the lines the chart exists to show.
        grid: { display: false },
        border: { color: performanceChartStyles.axisColor },
        ticks: {
          ...performanceTickFont,
          // Formatted from the LABEL rather than the tick index: autoSkip
          // renumbers the ticks it keeps, so an index into `dates` would
          // report the wrong day as soon as the axis is too crowded to draw
          // every one.
          callback(this: Scale, value: string | number) {
            return asUsDate(String(this.getLabelForValue(Number(value))));
          },
          maxRotation: 0,
          autoSkip: true,
          autoSkipPadding: 12,
        },
      },
      y: {
        type: "linear",
        // Fitted, or left to the library when there is nothing to fit to.
        ...(fitted ?? {}),
        // No guide at 0%: every series opens there, so a rule across the
        // plot at that height reads as a line of its own (operator, task
        // 5298). Its "0%" label and the other guides stay. The fitted range
        // always contains 0 and the linear scale's ticks are multiples of its
        // step, so it is a tick, and Chart.js skips a grid line of width 0.
        grid: {
          color: performanceChartStyles.gridColor,
          lineWidth: ({ tick }) => (tick?.value === 0 ? 0 : 1),
        },
        border: { display: false },
        ticks: {
          ...performanceTickFont,
          // The fitted bounds are a padded range, not a figure anyone reads —
          // labelled, they put "114.17%" immediately above the "112%" tick and
          // read as a value the series reached. Chart.js labels them because
          // the bounds were given explicitly, so it is told not to.
          includeBounds: false,
          callback: (value: string | number) =>
            performanceYAxisConfig.valueFormatter(Number(value)),
        },
      },
    },
  };
};

/** A line's key: its colour and dash pattern, drawn as the line itself is. */
const LineSwatch = ({
  line,
  sx,
}: {
  line: Pick<OverlayLine, "color" | "dash">;
  sx?: SxProps<Theme>;
}) => (
  <Box
    component="svg"
    aria-hidden
    width={18}
    height={4}
    viewBox="0 0 18 4"
    sx={sx}
  >
    <line
      x1={0}
      y1={2}
      x2={18}
      y2={2}
      stroke={line.color}
      strokeWidth={3}
      strokeDasharray={line.dash || undefined}
    />
  </Box>
);

/**
 * The hovered day, and what every line drawn on it was worth: its own move
 * that day and its compounded total, one row per line.
 *
 * A TABLE and not the single series' three figures it used to be — the chart
 * carries every selected series now, so the hovered day has as many answers
 * as there are lines. Columns rather than a labelled pair per line, so the
 * reader compares one series against the next straight down the column.
 *
 * Each day's move is read from the SERIES rather than derived from the plotted
 * totals: the totals compound, so differencing them would report the change
 * in the index instead of the move that produced it (a day taking 110 to
 * 115.5 moved 5%, not 5.5%).
 *
 * A line with no priced day at the hovered date is LEFT OUT rather than given
 * a blank row: it does not exist there, and its line is broken at that point
 * for the same reason.
 *
 * OURS, drawn as HTML over the canvas, rather than Chart.js' built-in tooltip:
 * the built-in one renders text on the canvas, so it can carry neither the
 * dashed swatch that ties a row to its line nor the aligned columns the
 * comparison is read down. Exported so it is tested as the component the chart
 * actually mounts — a canvas gives jsdom nothing to hover.
 */
export const PerformanceAxisTooltip = ({
  lines,
  dataIndex,
}: {
  lines: OverlayLine[];
  dataIndex: number | null;
}) => {
  if (dataIndex === null) return null;
  const rows = lines
    .map((line) => ({ line, point: line.points[dataIndex] }))
    .filter(
      (row): row is { line: OverlayLine; point: BucketPerformancePoint } =>
        Boolean(row.point),
    );
  if (rows.length === 0) return null;

  return (
    <Box sx={performanceChartStyles.tooltip}>
      <Typography sx={performanceChartStyles.tooltipDate}>
        {asUsDate(rows[0].point.date)}
      </Typography>
      <Box sx={performanceChartStyles.tooltipGrid}>
        <Box />
        <Box />
        <Typography sx={performanceChartStyles.tooltipColumnHeader}>
          Daily
        </Typography>
        <Typography sx={performanceChartStyles.tooltipColumnHeader}>
          Total
        </Typography>
        {rows.map(({ line, point }) => [
          <LineSwatch
            key={`${line.key}-swatch`}
            line={line}
            sx={performanceChartStyles.tooltipSwatch}
          />,
          <Typography
            key={`${line.key}-name`}
            sx={performanceChartStyles.tooltipSeriesName}
          >
            {line.title}
          </Typography>,
          <Typography
            key={`${line.key}-daily`}
            sx={performanceChartStyles.tooltipFigure}
          >
            {formatDaily(point.daily)}
          </Typography>,
          <Typography
            key={`${line.key}-total`}
            sx={performanceChartStyles.tooltipFigure}
          >
            {formatSigned(point.total)}
          </Typography>,
        ])}
      </Box>
    </Box>
  );
};

/**
 * ONE chart carrying every selected series: dates across the X axis, each
 * security's (or bucket's) cumulative return up the Y axis, opening at 0% and
 * moving up or down from it.
 *
 * OVERLAID, not one chart per series. Every series is the same measurement in
 * the same unit — a cumulative return opening at 0% — so putting them on shared axes is
 * what lets the reader compare them at all; stacked down the page, comparing
 * two securities meant reading two separate y-ranges against each other.
 *
 * Drawn by Chart.js on a canvas. Everything a reader reads as TEXT — the
 * legend, the tooltip, a refused series' reason — is real DOM around that
 * canvas rather than pixels on it: the palette leans on the legend as the
 * encoding of identity that is not colour, so it has to be reachable by a
 * screen reader, and the tooltip is a table no canvas tooltip can lay out.
 *
 * A series the backend refused shows its reason UNDER the plot rather than in
 * place of it: it no longer occupies a chart of its own, so the lines that did
 * come back are still drawn. With nothing drawable at all there is no plot —
 * an empty plot area reads as a period in which nothing happened.
 */
const PerformanceLineChart = ({ title, specs }: PerformanceLineChartProps) => {
  const { dates, lines, unavailable, notes, points } = useMemo(
    () => buildOverlayChartData(specs),
    [specs],
  );

  const [hovered, setHovered] = useState<HoveredDay | null>(null);

  // Guarded against re-setting the same day. Chart.js calls the external
  // tooltip handler on every draw, and an unguarded setState would re-render,
  // re-draw and call it again — a loop with no new information in it.
  const handleHover = useCallback((day: HoveredDay | null) => {
    setHovered((previous) => (sameDay(previous, day) ? previous : day));
  }, []);

  // Placed after it renders and before it paints: where the panel goes
  // depends on its own width, which only the laid-out panel knows.
  const anchorRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !hovered) return;
    const left = tooltipLeft({
      caretX: hovered.x,
      flip: hovered.flip,
      panelWidth: anchor.offsetWidth,
      plotWidth: anchor.parentElement?.clientWidth ?? 0,
    });
    anchor.style.left = `${left}px`;
    // `lines` too: a new selection re-sizes the panel under an unchanged caret.
  }, [hovered, lines]);

  const data = useMemo(
    () => buildPerformanceChartData(dates, lines),
    [dates, lines],
  );

  const options = useMemo(
    () => buildPerformanceChartOptions(points, handleHover),
    [points, handleHover],
  );

  return (
    <Box sx={performanceChartStyles.section}>
      <Box sx={chartsReportStyles.titleContainer}>
        <Box sx={chartsReportStyles.accentRectangle} />
        <Typography component="h3" sx={chartsReportStyles.title}>
          {title}
        </Typography>
      </Box>

      {dates.length > 0 && (
        <>
          <Box sx={performanceChartStyles.plot} data-testid="performance-plot">
            <Line data={data} options={options} />
            {hovered && (
              <Box
                ref={anchorRef}
                sx={performanceChartStyles.tooltipAnchor}
                // `left` is set by the layout effect above, once the panel's
                // width is known — so it is deliberately absent here, where
                // React would reset it on every render.
                style={{ top: hovered.y, transform: "translateY(-50%)" }}
              >
                <PerformanceAxisTooltip
                  lines={lines}
                  dataIndex={hovered.dataIndex}
                />
              </Box>
            )}
          </Box>
          {/*
            A LIST, and named. The palette leans on this legend as the
            encoding of identity that is not colour (see memberSeriesColors),
            so a reader who cannot see the strokes has to be able to reach it:
            as a bare stack of boxes it read out as a run of unlabelled
            paragraphs.
          */}
          <Box
            component="ul"
            aria-label={`${title} series`}
            sx={performanceChartStyles.legend}
          >
            {lines.map((line) => (
              <Box
                component="li"
                key={line.key}
                sx={performanceChartStyles.legendItem}
              >
                <LineSwatch line={line} />
                <Typography sx={performanceChartStyles.legendLabel}>
                  {line.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {notes.length > 0 && (
        <Box sx={performanceChartStyles.unavailableList}>
          {notes.map((line) => (
            <Typography
              key={line.key}
              sx={performanceChartStyles.unavailableRow}
            >{`${line.title}: ${line.note}`}</Typography>
          ))}
        </Box>
      )}

      {unavailable.length > 0 && (
        <Box sx={performanceChartStyles.unavailableList}>
          {unavailable.map((line) => (
            <Typography
              key={line.key}
              sx={performanceChartStyles.unavailableRow}
            >{`${line.title}: ${line.reason}`}</Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PerformanceLineChart;
